import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layouts
import MainLayout from './layouts/MainLayout';
import ProjectLayout from './layouts/ProjectLayout';

// Pages
import Login from './pages/auth/Login';
import OrgPicker from './pages/auth/OrgPicker';
import BillingPlaceholder from './pages/auth/BillingPlaceholder';
import Projects from './pages/dashboard/Projects';
import Team from './pages/dashboard/Team';
import Setup from './pages/project/Setup';
import Sessions from './pages/project/Sessions';
import ActiveSession from './pages/project/ActiveSession';
import Analysis from './pages/project/Analysis';
import Snapshots from './pages/project/Snapshots';
import SnapshotView from './pages/project/SnapshotView';
import ScreenerBuilder from './pages/project/ScreenerBuilder';
import ModuleBuilder from './pages/project/ModuleBuilder';
import ParticipantSession from './pages/participant/ParticipantSession';

const ProtectedRoute = ({ children }) => {
  const { user, loading, activeOrg, userOrgs } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div className="h-screen w-full flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
    </div>
  );

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!activeOrg && userOrgs.length > 1 && location.pathname !== '/org-picker') {
    return <Navigate to="/org-picker" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnimatePresence mode="wait">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/session/:participantToken" element={<ParticipantSession />} />

            {/* Protected Routes */}
            <Route path="/org-picker" element={<ProtectedRoute><OrgPicker /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><BillingPlaceholder /></ProtectedRoute>} />

            <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/projects" replace />} />
              <Route path="projects" element={<Projects />} />
              <Route path="team" element={<Team />} />
              <Route path="organizations" element={<div className="p-8 font-black text-2xl">Organization Settings</div>} />
            </Route>

            <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="setup" replace />} />
              <Route path="setup" element={<Setup />} />
              <Route path="screener" element={<ScreenerBuilder />} />
              <Route path="modules" element={<ModuleBuilder />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="sessions/:sessionId/active" element={<ActiveSession />} />
              <Route path="analysis" element={<Analysis />} />
              <Route path="snapshots" element={<Snapshots />} />
              <Route path="snapshots/:snapshotId" element={<SnapshotView />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </AnimatePresence>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;